import React, { useState, useEffect } from "react";
import { useMalware } from "../../../context/Dashboard/contextMalware";
import { useThemeMode } from "../../../context/contextTheme";
import { processData } from "../../../utils/processData";
import hexToRGBA from "../../../utils/hexToRGBA";
import {
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

const ChartGraph = () => {
  const { malAssets, duration, setDuration } = useMalware();
  const { theme } = useThemeMode();
  const [data, setData] = useState([]);

  useEffect(() => {
    const processed = processData(malAssets, duration);
    setData(processed);
  }, [malAssets, duration]);

  // Color palette aligned with Dashboard Status Badges
  const COLORS = {
    malicious: "#dc3545",
    suspicious: "#ffc107",
    harmless: "#198754",
    grid: hexToRGBA(theme.textColor, 0.05),
    text: theme.textMuted
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="shadow-lg border-0 rounded-3 p-3"
          style={{ backgroundColor: "#1d1d1d", color: "#fff", fontSize: "12px", minWidth: "160px" }}>
          <div className="mb-2 pb-1 border-bottom border-secondary opacity-75 fw-bold">
            {new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {payload.map((entry, index) => (
            <div key={index} className="d-flex justify-content-between align-items-center mb-1">
              <span className="me-3 d-flex align-items-center">
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, marginRight: 6 }} />
                {entry.name.toUpperCase()}
              </span>
              <span className="fw-bold">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className='card rounded-4 border-0 shadow-sm h-100' style={{ backgroundColor: theme.background }}>
      <div className='card-body d-flex flex-column p-4'>
        <div className="d-flex flex-row justify-content-between align-items-center mb-4">
          <div>
            <h6 className="fw-bold mb-0" style={{ color: theme.textColor }}>Threat Trends</h6>
            <small className="text-muted">Security scan results over time</small>
          </div>
          <select
            className="form-select form-select-sm border-0 shadow-none bg-light fw-bold"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            style={{ width: 140, cursor: 'pointer', color: '#344266' }}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="all">Total History</option>
          </select>
        </div>

        <div className="flex-grow-1" style={{ minHeight: '280px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                {/* Gradients for all three categories */}
                {['malicious', 'suspicious', 'harmless'].map(key => (
                  <linearGradient key={key} id={`color${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[key]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS[key]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
              <XAxis 
                dataKey="date" 
                tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                tick={{ fill: COLORS.text, fontSize: 10 }}
                axisLine={false} tickLine={false} dy={10}
              />
              <YAxis tick={{ fill: COLORS.text, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: COLORS.grid, strokeWidth: 2 }} />

              {/* Added Malicious Area */}
              <Area type="monotone" dataKey="malicious" stroke={COLORS.malicious} strokeWidth={3} fillOpacity={1} fill="url(#colormalicious)" />
              <Area type="monotone" dataKey="suspicious" stroke={COLORS.suspicious} strokeWidth={3} fillOpacity={1} fill="url(#colorsuspicious)" />
              <Area type="monotone" dataKey="harmless" stroke={COLORS.harmless} strokeWidth={3} fillOpacity={1} fill="url(#colorharmless)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="d-flex justify-content-center gap-4 mt-4">
          {['MALICIOUS', 'SUSPICIOUS', 'HARMLESS'].map(label => (
            <div key={label} className="d-flex align-items-center small fw-bold" style={{ color: COLORS[label.toLowerCase()] }}>
              <span className="me-2" style={{ width: 12, height: 4, borderRadius: 2, background: COLORS[label.toLowerCase()] }} />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChartGraph;